import { useInfiniteQuery, useQuery } from 'react-query'

import { api } from '../../../api'
import { type FiltersDomain } from '../../../../types/domain/filters-domain'
import { getValidFilters } from '../../../../utils/filters'
import { getQueryStrinfied } from '../../../../utils/query'
import { type AssetAnalystDomain } from '../../../../types/domain/asset-analyst-domain'
import type PaginationDomain from '../../../../types/domain/pagination-domain'

const resolveAssetsQueryFilters = (filters: FiltersDomain): FiltersDomain => ({
  ...filters,
  sort: filters.sort ?? 'fullName',
  order: filters.order ?? 'ASC',
})

export const fetchAssets = async (filters: FiltersDomain) => {
  const query = getQueryStrinfied(getValidFilters(resolveAssetsQueryFilters(filters)))
  const response = await api.get(`/assets?${query}`)
  return response.data
}

const fetchAnalystsByAssetId = async (id: number, filters: FiltersDomain) => {
  const query = getQueryStrinfied(getValidFilters(filters))
  const response = await api.get<PaginationDomain<AssetAnalystDomain>>(
    `/assets/${id}/analysts?${query}`
  )
  return response.data
}

export const useAssetsQuery = (filters: FiltersDomain) => {
  const resolvedFilters = resolveAssetsQueryFilters(filters)
  return useQuery(['assets', resolvedFilters], () => fetchAssets(filters), {
    staleTime: 1000 * 60 * 60 * 1 /* 1 hour */,
    keepPreviousData: true,
  })
}

export const useInfiniteAssetsQuery = (filters: FiltersDomain) => {
  const resolvedFilters = resolveAssetsQueryFilters(filters)
  return useInfiniteQuery(
    ['assets', resolvedFilters],
    ({ pageParam = 1 }) => fetchAssets({ ...filters, page: pageParam }),
    {
      staleTime: 1000 * 60 * 60 * 1, // 1 hour
      getNextPageParam ({ meta }) {
        return meta.hasNextPage ? meta.page + 1 : undefined
      },
    }
  )
}

export const useInfiniteAnalystsByAssetQuery = (
  id: number,
  filters: FiltersDomain
) => {
  return useInfiniteQuery(
    ['assets', id, 'analysts', filters],
    ({ pageParam = 1 }) =>
      fetchAnalystsByAssetId(id, { ...filters, page: pageParam }),
    {
      staleTime: 1000 * 60 * 60 * 1, // 1 hour
      getNextPageParam ({ meta }) {
        return meta.hasNextPage ? meta.page + 1 : undefined
      },
    }
  )
}

export const useAnalystsByAssetQuery = (id: number, filters: FiltersDomain) => {
  return useQuery(
    ['assets', id, 'analysts', filters],
    () => fetchAnalystsByAssetId(id, filters),
    {
      staleTime: 1000 * 60 * 60 * 1 /* 1 hour */,
      keepPreviousData: true,
    }
  )
}
